import _ from "lodash";

import api from "@/api/cloud";
import { get, set } from "@/utils/vuex";
import * as k4util from "@/utils/k4util";

const getInitState = () => ({
	items: [],
	excluded: [],
	rootNode: null,
});

export default {
	namespaced: true,

	state: getInitState(),

	getters: {
		rootNode: get("rootNode"),

		/**
		 * Collections
		 * -----------
		 */

		// Filter out devices that aren't relevant in the context of a
		// technicial pairing/testing devices during an install session.
		items: (state, getters, rootState) => {
			// filter using the exclusion list from descriptions, and rooms
			const excluded = rootState.descriptions.exclude;
			return _.sortBy(
				_.filter(
					state.items,
					device => excluded.indexOf(device.description.meta.group) === -1
				),
				"name" // Sort by name
			);
		},

		// Collection of rooms with nested devices
		rooms: (state, getters) =>
			_.map(
				_.filter(getters.items, { cls: k4util.DEVICE_CLASS_ROOM }),
				room => ({
					...room,
					children: _.filter(getters.items, { parent: room.guid }),
				})
			),

		// Collection of all devices that are not rooms
		devices: (state, getters) =>
			_.differenceWith(
				getters.items,
				getters.rooms,
				(a, b) => a.guid === b.guid
			),

		// All paired devices
		pairedDevices: (state, getters) =>
			_.filter(getters.devices, { configured: true }),

		// Are all devices paired?
		allPaired: (state, getters) =>
			_.every(getters.devices, { configured: true }),

		/**
		 * Selection
		 * ---------
		 * Methods for retrieving a single item
		 */

		itemByGuid: (state, getters) => guid => _.find(getters.items, { guid }),
		itemByPath: (state, getters) => path => _.find(getters.items, { path }),

		/**
		 * Item properties
		 * ---------------
		 * Retrieve a property of a single item while maintaining reactivity
		 */

		// Wether the given device is paired
		itemProp: (state, getters) => (guid, prop) =>
			_.get(getters.itemByGuid(guid), prop),

		isPaired: (state, getters) => guid =>
			getters.itemProp(guid, "configured") || undefined,
	},

	mutations: {
		setRootNode: set("rootNode"),

		setDevices(state, items) {
			if (_.isObject(items)) {
				items = _.values(items);
			}
			state.items = items;
		},

		removeDevice(state, guid) {
			const idx = _.findIndex(state.items, { guid: guid });
			state.items.splice(idx, 1);
		},

		resetState(state) {
			Object.assign(state, getInitState());
		},
	},

	actions: {
		async load({ commit, rootGetters }) {
			const system = rootGetters["session/system"];
			if (!system) return;

			this._vm.$log(`Devices: Loading devices for ${system.name}`);

			try {
				const devices = await api.getDevices(system.id);
				commit("setDevices", devices);
				const rootNode = await api.getRootNode(system.id);
				commit("setRootNode", rootNode);
			} catch (e) {
				commit("setDevices", []);
				throw e;
			}
		},
	},
};
